import { Token } from 'blast-mainnet-few-sdk-core-2'
import { utils } from 'ethers/lib'
import BlastIFewWrappedTokenJSON from './abis/BlastSepoliaFewWrappedToken.json'
import BlastIMainnetFewWrappedTokenJSON from './abis/FewWrappedToken.json'
import EVMIFewWrappedTokenJSON from './abis/EVMFewWrappedToken.json'

type ChainConfig = {
  router: string
  bytecode: string
}

const FEW_WRAPPED_FACTORY_CONFIGS: { [key: number]: ChainConfig } = {
  [168587773]: {
    router: '0xf11788d14EbE6abF4EA02e162C75AD938F1730C1',
    bytecode: BlastIFewWrappedTokenJSON.bytecode
  },
  [11155111]: {
    router: '0x226e65279E177A779522864Ce1dE40c85E2C08A5',
    bytecode: EVMIFewWrappedTokenJSON.bytecode
  },
  [81457]: {
    router: '0x455b20131D59f01d082df1225154fDA813E8CeE9',
    bytecode: BlastIMainnetFewWrappedTokenJSON.bytecode
  }
}

export const FEW_WRAPPED_TOKEN_FACTORY_ADDRESS = (chainId: number): string => {
  if (!(chainId in FEW_WRAPPED_FACTORY_CONFIGS)) {
    return FEW_WRAPPED_FACTORY_CONFIGS[81457].router
  } else {
    return FEW_WRAPPED_FACTORY_CONFIGS[chainId].router
  }
}

export const FEW_WRAPPED_TOKEN_BYTECODE = (chainId: number): string => {
  if (!(chainId in FEW_WRAPPED_FACTORY_CONFIGS)) {
    return FEW_WRAPPED_FACTORY_CONFIGS[81457].bytecode
  } else {
    return FEW_WRAPPED_FACTORY_CONFIGS[chainId].bytecode
  }
}

class FewTokenCreator {
  private token: Token
  private chainId: number

  constructor(token: Token, chainId: number) {
    this.token = token
    this.chainId = chainId
  }

  private getFewCreate2Address(token: string, chainId: number): string {
    const constructorArgumentsEncoded = utils.defaultAbiCoder.encode(['address'], [token])
    const create2Inputs = [
      '0xff',
      FEW_WRAPPED_TOKEN_FACTORY_ADDRESS(chainId),
      // salt
      utils.keccak256(constructorArgumentsEncoded),
      // init code. bytecode + constructor arguments
      utils.keccak256(FEW_WRAPPED_TOKEN_BYTECODE(chainId))
    ]
    const sanitizedInputs = `0x${create2Inputs.map(i => i.slice(2)).join('')}`
    return utils.getAddress(`0x${utils.keccak256(sanitizedInputs).slice(-40)}`)
  }

  private hasFewPrefix(): boolean {
    const isFewWrappedName = this.token.name?.startsWith('Few Wrapped ') ?? false
    const isFwSymbol = this.token.symbol?.startsWith('fw') ?? false
    return isFewWrappedName || isFwSymbol
  }

  private getFewTokenAddress(): string {
    if (this.hasFewPrefix()) {
      return this.token.address
    } else {
      return this.getFewCreate2Address(this.token.address, this.chainId)
    }
  }

  private getFewSymbol(): string | undefined {
    if (this.hasFewPrefix()) {
      return this.token.symbol
    } else {
      return `fw${this.token.symbol}`
    }
  }

  private getFewTokenName(): string | undefined {
    if (this.hasFewPrefix()) {
      return this.token.name
    } else {
      return `Few Wrapped ${this.token.name}`
    }
  }

  getFewToken(): Token {
    const fewTokenAddress = this.getFewTokenAddress()
    return new Token(
      this.token.chainId,
      fewTokenAddress,
      this.token.decimals,
      this.getFewSymbol(),
      this.getFewTokenName()
    )
  }
}

export function getFewTokenFromOriginalToken(token: Token, chainId: number): Token {
  const fewTokenCreator = new FewTokenCreator(token, chainId)
  return fewTokenCreator.getFewToken()
}
